import React from "react";
import { useSearchParams } from "react-router-dom";

// Portfolio.js
const categories = [
  { key: "all", label: "All Work" },
  { key: "lifestyle", label: "Lifestyle Portraits" },
  { key: "portraits", label: "Portraits" },
  { key: "maternity", label: "Maternity" },
  { key: "newborn", label: "Newborn" },
  { key: "weddings", label: "Weddings" },
];

function Portfolio() {
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category") || "all";
  
  const [activeCategory, setActiveCategory] = React.useState(categoryFromUrl);
  const [allImages, setAllImages] = React.useState([]);
  const [filteredImages, setFilteredImages] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasMoreImages, setHasMoreImages] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Refs to track loading state and prevent race conditions
  const isLoadingRef = React.useRef(false);
  const hasMoreRef = React.useRef(true);
  const currentPageRef = React.useRef(1);
  const activeCategoryRef = React.useRef("all");

  const IMAGES_PER_PAGE = 8;

  // Function to load images from Cloudinary
  const loadMoreImages = React.useCallback(async () => {
    // Use refs to check loading state and prevent race conditions
    if (isLoadingRef.current || !hasMoreRef.current) {
      console.log("Skipping load:", {
        isLoading: isLoadingRef.current,
        hasMore: hasMoreRef.current,
      });
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    console.log("🚀 Loading images:", {
      page: currentPageRef.current,
      category: activeCategoryRef.current,
    });

    try {
      // API endpoint that handles Cloudinary integration
      const queryParams = new URLSearchParams({
        page: currentPageRef.current,
        limit: IMAGES_PER_PAGE,
        ...(activeCategoryRef.current !== "all" && {
          category: activeCategoryRef.current,
        }),
      });

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
      const response = await fetch(
        `${backendUrl}/api/images?${queryParams}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Received data:", data);

      const { images, hasMore } = data;

      if (!images || images.length === 0) {
        console.log("No images received");
        setHasMoreImages(false);
        hasMoreRef.current = false;
        setTimeout(() => {
          isLoadingRef.current = false;
          setIsLoading(false);
        }, 0);
        return;
      }

      // Add new images with duplicate prevention using Set for O(1) lookup
      setAllImages((prev) => {
        const existingIds = new Set(prev.map((img) => img.id));
        const newImages = images.filter((img) => !existingIds.has(img.id));
        console.log("📦 Adding images:", {
          received: images.length,
          alreadyHave: prev.length,
          newToAdd: newImages.length,
        });
        return [...prev, ...newImages];
      });

      // Update page and hasMore
      currentPageRef.current += 1;
      hasMoreRef.current = hasMore;

      setHasMoreImages(hasMore);

      console.log("✅ Load complete:", {
        nextPage: currentPageRef.current,
        hasMore,
        imagesAdded: images.length,
      });

      // CRITICAL: Reset loading AFTER all state updates
      setTimeout(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
      }, 0);
    } catch (err) {
      console.error("Failed to load images:", err);
      setError("Failed to load images. Please try again.");
      setTimeout(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
      }, 0);
    }
  }, []);

  // Update category ref when it changes
  React.useEffect(() => {
    activeCategoryRef.current = activeCategory;
  }, [activeCategory]);

  // Load initial images on component mount and when category changes
  React.useEffect(() => {
    console.log("🔄 Category changed to:", activeCategory);
    // Reset ALL state when category changes
    setAllImages([]);
    setFilteredImages([]);
    currentPageRef.current = 1;
    setHasMoreImages(true);
    hasMoreRef.current = true;
    setError(null);

    // Force reset loading state
    isLoadingRef.current = false;
    setIsLoading(false);

    // Small delay to ensure state is reset before loading
    const timer = setTimeout(() => {
      loadMoreImages();
    }, 100);

    return () => clearTimeout(timer);
  }, [activeCategory, loadMoreImages]);

  // Infinite scroll logic
  React.useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 1000
      ) {
        loadMoreImages();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMoreImages]);

  // Update filtered images when allImages changes
  React.useEffect(() => {
    console.log("📸 Updating filtered images:", allImages.length);
    setFilteredImages(allImages);
  }, [allImages]);

  const getGridRowSpan = (height) => {
    switch (height) {
      case "short":
        return "row-span-3";
      case "square":
        return "row-span-4";
      case "medium":
        return "row-span-5";
      case "tall":
        return "row-span-6";
      default:
        return "row-span-4";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl font-light text-gray-800 mb-2 sm:mb-4">
            Photography Portfolio
          </h1>
          <p className="text-gray-600 text-base sm:text-lg">
            Capturing life's beautiful moments
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 px-2">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setActiveCategory(category.key)}
              className={`px-3 py-2 sm:px-6 sm:py-2 text-sm sm:text-base rounded-full transition-all duration-300 ${
                activeCategory === category.key
                  ? "bg-gray-800 text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-gray-100 shadow-md active:bg-gray-200"
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-center mb-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg inline-block">
              {error}
              <button
                onClick={() => loadMoreImages()}
                className="ml-2 underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Image Count */}
        <div className="text-center mb-6">
          <p className="text-gray-500 text-sm">
            Showing {filteredImages.length}{" "}
            {activeCategory === "all" ? "images" : `${activeCategory} images`}
            {hasMoreImages && !error && (
              <span className="ml-2">• Scroll for more</span>
            )}
          </p>
        </div>

        {/* Masonry Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 auto-rows-[60px] sm:auto-rows-[80px]">
          {filteredImages.map((image) => (
            <div
              key={image.id}
              className={`group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer active:scale-95 ${getGridRowSpan(
                image.height
              )}`}
            >
              <img
                src={image.blobUrl}
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
                onError={(e) => {
                  e.target.src =
                    "https://placehold.co/400x400?text=Image+Not+Found";
                }}
              />

              {/* Subtle border on hover */}
              <div className="absolute inset-0 border-2 border-white opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-lg"></div>
            </div>
          ))}
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="text-center mt-8 sm:mt-12">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm">Loading more images...</span>
            </div>
          </div>
        )}

        {/* No Images Message */}
        {!isLoading && filteredImages.length === 0 && !error && (
          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-500 text-lg">
              No images found for this category.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Portfolio;
